import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { api, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, Bell, LineChart, Clock, ShieldCheck, ArrowUpRight, Check } from "lucide-react";

const FEATURES = [
  { icon: Radio, t: "Real-time alerts", d: "Asset, direction, entry/exit levels and live status — the moment Q-Core surfaces an opportunity." },
  { icon: LineChart, t: "Derived from real markets", d: "Signals are computed from genuine cross-exchange and derivatives data, not hand-waving." },
  { icon: Clock, t: "Continuous feed", d: "A live, auto-updating stream inside your dashboard — no refreshing, no waiting." },
  { icon: ShieldCheck, t: "Standalone product", d: "A paid information product. No deposit, no investment, no custody of your funds." },
];

export default function SignalsProduct() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  useEffect(() => { trackEvent("page_view", { page: "signals_product" }); }, []);
  const buy = async () => {
    if (!user) { nav("/register"); return; }
    setBusy(true);
    try { const { data } = await api.post("/payments/checkout", { lookup_key: "quantum_signals", origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch { setBusy(false); }
  };
  return (
    <div>
      <PageHeader eyebrow="Quantum Signals · Information Product"
        title="Real-time signal alerts, straight from the engine."
        sub="A standalone subscription to Q-Core's live trading signals — asset, direction, and entry/exit levels delivered continuously. No deposit. No investment. Just the intelligence.">
        <button onClick={buy} disabled={busy} data-testid="signals-buy" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium shadow-[0_0_24px_rgba(14,158,144,0.35)] hover:bg-[#1E4BCC] disabled:opacity-60 transition-colors">
          {busy ? "Starting…" : "Subscribe — $99/mo"} <ArrowUpRight size={17} />
        </button>
      </PageHeader>

      <Section grid>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.05}>
              <div className="qs-beam h-full rounded-xl border border-white/5 bg-[#0A0B10] p-6">
                <span className="grid place-items-center h-11 w-11 rounded-lg border border-[#0E9E90]/30 bg-[#0E9E90]/10 text-[#2DD4BF] mb-4"><f.icon size={20} strokeWidth={1.5} /></span>
                <h3 className="font-bold text-white">{f.t}</h3>
                <p className="text-sm text-[#94A3B8] mt-1.5 leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section grid>
        <div className="rounded-2xl border border-[#0E9E90]/25 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-8 md:p-12 qs-aurora">
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <SectionHeading eyebrow="What you get" title="Everything in the live feed." />
              <ul className="mt-6 space-y-2.5">
                {["Live signal cards with entry, target and stop", "Direction (long/short) and strategy tag", "Confidence indicator and source venue", "Cross-exchange and perpetual-basis coverage", "Cancel anytime — it's a subscription, not a commitment"].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-sm text-[#cbd5e1]"><Check size={16} className="text-[#00E676] mt-0.5 shrink-0" /> {x}</li>
                ))}
              </ul>
            </div>
            <div className="text-center rounded-2xl border border-white/10 bg-black/30 p-8">
              <Bell size={30} className="text-[#2DD4BF] mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-mono text-4xl text-white">$99<span className="text-base text-[#64748B]">/month</span></p>
              <button onClick={buy} disabled={busy} data-testid="signals-buy-2" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium hover:bg-[#1E4BCC] disabled:opacity-60 transition-colors">
                {busy ? "Starting…" : (user ? "Subscribe now" : "Create account & subscribe")} <ArrowUpRight size={16} />
              </button>
              <p className="mt-4 text-[11px] text-[#64748B] font-mono">Informational technology output. Not financial advice. Markets carry risk; results are not guaranteed.</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
